import { createCachedSelector } from 're-reselect';
import { useContext } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import moment from 'moment';
import * as api from '../../services/api';
import useCRUD from '../useCRUD';
import { QUERY_CACHE_TIME } from '../../constants';
import useToaster from '../useToaster';
import { mappedArray } from '../../utils/array';
import useAccount from './useAccount';

const selector = createCachedSelector(
  (data) => data.data,
  (data, t) => t,
  (data, t, dateFormat) => dateFormat,
  (data, t, dateFormat, storeKey) => storeKey,
  (data, t, dateFormat) => {
    const contracts = (data || []).map((contract) => ({
      ...contract,
      createdAt: moment(contract.createdAt).format(dateFormat || 'DD/MM/YYYY')
    }));

    const mappedContracts = mappedArray(contracts || []);

    return { contracts, mappedContracts };
  }
)({
  keySelector: (data, t, dateFormat, storeKey) => storeKey
});

const useContracts = () => {
  const { t, dateFormat } = useContext(ConfigContext);
  const { accountId } = useAccount();
  const queryClient = useQueryClient();
  const { addResponseError, addSuccess } = useToaster();

  const storeKey = ['contracts', accountId];
  const { isLoading, data } = useQuery(storeKey, async () => api.fetchContracts({ accountId }), {
    enabled: !!accountId,
    select: (contractsData) => selector(contractsData, t, dateFormat, storeKey.join('#')),
    cacheTime: QUERY_CACHE_TIME
  });

  const { contracts = [], mappedContracts = {} } = data || {};

  const { addItem, editItem, deleteItem } = useCRUD(
    storeKey,
    {
      addApi: (contract) => api.addContract({ accountId, ...contract }),
      editApi: (contract) => api.updateContract({ accountId, contractId: contract.id, ...contract }),
      deleteApi: async ({ id }) => {
        try {
          const result = await api.deleteContract({ accountId, contractId: id });
          if (result) {
            addSuccess(t('contractDeletedSuccessfully'));
          }
          return result;
        } catch (e) {
          addResponseError(e);
        }
      }
    },
    { onSuccess: () => queryClient.invalidateQueries(storeKey) }
  );

  return {
    isLoading,
    contracts,
    mappedContracts,
    addContract: addItem,
    editContract: editItem,
    deleteContract: deleteItem
  };
};

export default useContracts;
